import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useData } from "@/lib/dataStore";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Buildings, Plant, IdentificationCard, CloudArrowUp, ShieldCheck, Key, SignOut, CheckCircle, XCircle, Rocket, Lock, Broom, Warning } from "@phosphor-icons/react";

export default function SettingsDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { settings, refreshSettings } = useData();

  const [biz, setBiz] = useState({ business_name: "", org_number: "", moms_id: "", address: "", seed_per_tray_ratio: 0.25 });
  const [scaleway, setScaleway] = useState({ access_key: "", secret_key: "", bucket: "", region: "fr-par", endpoint_url: "" });
  const [scalewaySecretSet, setScalewaySecretSet] = useState(false);
  const [nas, setNas] = useState({ label: "Personal NAS", endpoint_url: "", access_key: "", secret_key: "", bucket: "", region: "us-east-1", verify_tls: true });
  const [nasSecretSet, setNasSecretSet] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [emailHealth, setEmailHealth] = useState(null); // {healthy, problems[], resend_from, is_sandbox_from}
  const [testingEmail, setTestingEmail] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "", target_email: "" });
  const [savingBiz, setSavingBiz] = useState(false);
  const [savingScw, setSavingScw] = useState(false);
  const [savingNas, setSavingNas] = useState(false);
  const [savingMfa, setSavingMfa] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [testingScw, setTestingScw] = useState(false);
  const [testingNas, setTestingNas] = useState(false);
  const [scwStatus, setScwStatus] = useState(null);
  const [nasStatus, setNasStatus] = useState(null);

  // Admin user management
  const [adminList, setAdminList] = useState([]);
  const [masterEmail, setMasterEmail] = useState("");
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminChallenge, setAdminChallenge] = useState(null);
  const [adminCode, setAdminCode] = useState("");
  const isMasterAdmin = (user?.email || "").toLowerCase() === (masterEmail || "").toLowerCase();

  const refreshAdmins = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setAdminList(data.users || []);
      setMasterEmail(data.master_admin_email || "");
      setPw((p) => ({ ...p, target_email: p.target_email || user?.email || "" }));
    } catch (_) { /* endpoint may not exist on older backend */ }
  };

  const refreshEmailHealth = async () => {
    try {
      const { data } = await api.get("/settings/email-health");
      setEmailHealth(data);
    } catch (_) { /* master-only endpoint — silent for non-master */ }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      const { data } = await api.post("/settings/email-test");
      toast.success(`Test email sent to ${data.sent_to}. Check your inbox (also spam).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test email failed");
    } finally {
      setTestingEmail(false);
    }
  };

  // Production Start (BFL immutability gate)
  const [prodPwd, setProdPwd] = useState("");
  const [prodStartDate, setProdStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [prodChallenge, setProdChallenge] = useState(null); // {challenge_id, email_hint}
  const [prodCode, setProdCode] = useState("");
  const [prodBusy, setProdBusy] = useState(false);
  const productionStartedAt = settings?.production_started_at;
  const retentionUntil = settings?.retention_required_until;

  // Pre-Production Reset (wipes TEST sales only, keeps expenses)
  const [resetPreview, setResetPreview] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [resetPhrase, setResetPhrase] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetExpanded, setResetExpanded] = useState(false);

  useEffect(() => {
    if (settings && open) {
      refreshAdmins();
      refreshEmailHealth();
      setBiz({
        business_name: settings.business_name || "",
        org_number: settings.org_number || "",
        moms_id: settings.moms_id || "",
        address: settings.address || "",
        seed_per_tray_ratio: settings.seed_per_tray_ratio ?? 0.25,
      });
      const sc = settings.scaleway || {};
      setScaleway({
        access_key: sc.access_key || "",
        secret_key: "",
        bucket: sc.bucket || "",
        region: sc.region || "fr-par",
        endpoint_url: sc.endpoint_url || "",
      });
      setScalewaySecretSet(!!sc.secret_key_set);
      const n = settings.nas || {};
      setNas({
        label: n.label || "Personal NAS",
        endpoint_url: n.endpoint_url || "",
        access_key: n.access_key || "",
        secret_key: "",
        bucket: n.bucket || "",
        region: n.region || "us-east-1",
        verify_tls: n.verify_tls !== false,
      });
      setNasSecretSet(!!n.secret_key_set);
      setMfaEnabled(!!settings.mfa_enabled);
    }
  }, [settings, open]);

  const saveBusiness = async () => {
    setSavingBiz(true);
    try {
      await api.put("/settings", {
        business_name: biz.business_name,
        org_number: biz.org_number,
        moms_id: biz.moms_id,
        address: biz.address,
        seed_per_tray_ratio: parseFloat(biz.seed_per_tray_ratio) || 0.25,
      });
      await refreshSettings();
      toast.success("Business details saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingBiz(false); }
  };

  const saveScaleway = async () => {
    setSavingScw(true);
    try {
      await api.put("/settings", { scaleway });
      await refreshSettings();
      toast.success("Scaleway settings saved");
      setScaleway((s) => ({ ...s, secret_key: "" }));
      setScalewaySecretSet(true);
    } catch { toast.error("Save failed"); }
    finally { setSavingScw(false); }
  };

  const testScaleway = async () => {
    setTestingScw(true);
    setScwStatus(null);
    try {
      const { data } = await api.post("/storage/scaleway/test");
      setScwStatus({ ok: true, msg: `Connected to ${data.bucket}` });
      toast.success(`Connected to ${data.bucket}`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setScwStatus({ ok: false, msg });
      toast.error(msg);
    } finally { setTestingScw(false); }
  };

  const saveNas = async () => {
    setSavingNas(true);
    try {
      await api.put("/settings", { nas });
      await refreshSettings();
      toast.success("NAS settings saved");
      setNas((s) => ({ ...s, secret_key: "" }));
      setNasSecretSet(true);
    } catch { toast.error("Save failed"); }
    finally { setSavingNas(false); }
  };

  const testNas = async () => {
    setTestingNas(true);
    setNasStatus(null);
    try {
      const { data } = await api.post("/storage/nas/test");
      setNasStatus({ ok: true, msg: `Connected — ${data.label} · ${data.endpoint} · bucket ${data.bucket}` });
      toast.success(`NAS reachable: ${data.bucket}`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setNasStatus({ ok: false, msg });
      toast.error(msg);
    } finally { setTestingNas(false); }
  };

  const toggleMfa = async (checked) => {
    if (checked && emailHealth && !emailHealth.healthy) {
      const problems = (emailHealth.problems || []).join("\n• ");
      toast.error(
        `Cannot enable MFA — email delivery is broken. Fix first:\n• ${problems}`,
        { duration: 10000 }
      );
      return;
    }
    setSavingMfa(true);
    try {
      await api.put("/settings", { mfa_enabled: checked });
      setMfaEnabled(checked);
      await refreshSettings();
      toast.success(checked ? "MFA enabled — next login will require email code" : "MFA disabled");
    } catch { toast.error("Could not update MFA"); }
    finally { setSavingMfa(false); }
  };

  const changePassword = async () => {
    if (pw.new_password !== pw.confirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    if (pw.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password, target_email: pw.target_email || undefined });
      toast.success(pw.target_email && pw.target_email !== user?.email ? `Password changed for ${pw.target_email}` : "Password changed");
      setPw({ current_password: "", new_password: "", confirm: "", target_email: user?.email || "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Password change failed");
    } finally { setSavingPw(false); }
  };

  const promptMasterPassword = () => {
    const p1 = window.prompt("Master MFA isn't configured.\n\nRe-enter your master password to authorize this action:") || "";
    if (!p1) return "";
    const p2 = window.prompt("Confirm — re-enter the same password to proceed:") || "";
    if (p1 !== p2) { toast.error("Passwords did not match — action cancelled"); return ""; }
    return p1;
  };

  const requestAddAdmin = async () => {
    const email = (newAdminEmail || "").trim().toLowerCase();
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setAdminBusy(true);
    try {
      const { data } = await api.post("/admin/users/add/request", { email });
      setAdminChallenge({ ...data, mode: "add", target_email: email });
      toast.success(`Code sent to ${data.email_hint}`);
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (e.response?.status === 400 && detail.toLowerCase().includes("not configured")) {
        const pwd = promptMasterPassword();
        if (!pwd) { setAdminBusy(false); return; }
        try {
          await api.post("/admin/users/add-with-password", { email, password: pwd });
          toast.success(`Added ${email}`);
          setNewAdminEmail("");
          refreshAdmins();
        } catch (e2) {
          toast.error(e2.response?.data?.detail || "Add failed");
        }
      } else {
        toast.error(detail || "Failed to send code");
      }
    } finally { setAdminBusy(false); }
  };

  const requestDeleteAdmin = async (email) => {
    if (!window.confirm(`Remove admin ${email}?`)) return;
    setAdminBusy(true);
    try {
      const { data } = await api.post("/admin/users/delete/request", { email });
      setAdminChallenge({ ...data, mode: "delete", target_email: email });
      toast.success(`Code sent to ${data.email_hint}`);
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (e.response?.status === 400 && detail.toLowerCase().includes("not configured")) {
        const pwd = promptMasterPassword();
        if (!pwd) { setAdminBusy(false); return; }
        try {
          await api.post("/admin/users/delete-with-password", { email, password: pwd });
          toast.success(`Removed ${email}`);
          refreshAdmins();
        } catch (e2) {
          toast.error(e2.response?.data?.detail || "Remove failed");
        }
      } else {
        toast.error(detail || "Failed to send code");
      }
    } finally { setAdminBusy(false); }
  };

  const confirmAdminChange = async () => {
    if (!adminChallenge || adminCode.length < 6) { toast.error("Enter the 6-digit code"); return; }
    setAdminBusy(true);
    const path = adminChallenge.mode === "add" ? "/admin/users/add/confirm" : "/admin/users/delete/confirm";
    try {
      await api.post(path, { challenge_id: adminChallenge.challenge_id, code: adminCode });
      toast.success(adminChallenge.mode === "add" ? `Added ${adminChallenge.target_email}` : `Removed ${adminChallenge.target_email}`);
      setNewAdminEmail(""); setAdminCode(""); setAdminChallenge(null);
      refreshAdmins();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Confirmation failed");
    } finally { setAdminBusy(false); }
  };

  const doLogout = async () => {
    onOpenChange(false);
    await logout();
    navigate("/login", { replace: true });
  };

  const requestProductionStart = async () => {
    if (!prodPwd) { toast.error("Enter your password"); return; }
    setProdBusy(true);
    try {
      const { data } = await api.post("/admin/production-start/request", { password: prodPwd });
      setProdChallenge(data);
      toast.success(`Verification code sent to ${data.email_hint}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to request code");
    } finally {
      setProdBusy(false);
    }
  };

  const confirmProductionStart = async () => {
    if (!prodCode || prodCode.length < 6) { toast.error("Enter the 6-digit code"); return; }
    setProdBusy(true);
    try {
      const { data } = await api.post("/admin/production-start/confirm", {
        challenge_id: prodChallenge.challenge_id,
        code: prodCode,
        start_date: prodStartDate,
      });
      toast.success(`Production started · data locked until ${data.retention_required_until}`);
      setProdPwd(""); setProdCode(""); setProdChallenge(null);
      refreshSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Confirmation failed");
    } finally {
      setProdBusy(false);
    }
  };

  const loadResetPreview = async () => {
    setResetExpanded(true);
    try {
      const { data } = await api.get("/admin/pre-production-reset/preview");
      setResetPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot load preview");
    }
  };

  const doPreProductionReset = async () => {
    if (!resetPwd) { toast.error("Enter your password"); return; }
    if (resetPhrase !== "RESET TEST DATA") { toast.error('Type exactly: RESET TEST DATA'); return; }
    setResetBusy(true);
    try {
      const { data } = await api.post("/admin/pre-production-reset", {
        password: resetPwd,
        confirm_phrase: resetPhrase,
      });
      toast.success(
        `Wiped ${data.deleted_sales} test sale(s), ${data.deleted_receipts} receipt(s), ${data.deleted_expenses ?? 0} expense(s). ` +
        `Rebuilt ledger with ${data.rebuilt_vouchers} voucher(s).`
      );
      setResetPwd(""); setResetPhrase(""); setResetPreview(null); setResetExpanded(false);
      refreshSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Settings</DialogTitle>
          <DialogDescription className="text-slate-500">
            Manage your business identity, security, cloud backups, and account.
          </DialogDescription>
        </DialogHeader>

        {/* Business Identity */}
        <Section title="Business Identity" Icon={Buildings} testId="settings-section-business">
          <p className="text-xs text-slate-500 -mt-2">Shown on every printed &amp; emailed receipt.</p>
          <Field label="Org. Name">
            <Input value={biz.business_name} onChange={(e) => setBiz({ ...biz, business_name: e.target.value })} placeholder="The NordHim Ledger AB" data-testid="settings-business-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Org. Number" Icon={IdentificationCard}>
              <Input value={biz.org_number} onChange={(e) => setBiz({ ...biz, org_number: e.target.value })} placeholder="556677-8899" data-testid="settings-org-number" />
            </Field>
            <Field label="Moms ID" Icon={IdentificationCard}>
              <Input value={biz.moms_id} onChange={(e) => setBiz({ ...biz, moms_id: e.target.value })} placeholder="SE556677889901" data-testid="settings-moms-id" />
            </Field>
          </div>
          <Field label="Address">
            <Textarea rows={2} value={biz.address} onChange={(e) => setBiz({ ...biz, address: e.target.value })} placeholder="Storgatan 1, 111 22 Stockholm, Sweden" data-testid="settings-address" />
          </Field>
          <Field label="Seed per Tray Ratio (kg)" Icon={Plant}>
            <Input type="number" step="0.01" min="0.01" value={biz.seed_per_tray_ratio} onChange={(e) => setBiz({ ...biz, seed_per_tray_ratio: e.target.value })} data-testid="settings-ratio" />
            <p className="text-xs text-slate-500 mt-1">Default 0.25 kg. Drives sale-time grain deduction.</p>
          </Field>
          <div className="flex justify-end">
            <Button onClick={saveBusiness} disabled={savingBiz} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="settings-save-business">
              {savingBiz ? "Saving…" : "Save Business Details"}
            </Button>
          </div>
        </Section>

        {/* Security: MFA */}
        <Section title="Security" Icon={ShieldCheck} testId="settings-section-security">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="font-medium text-slate-900">Email MFA</div>
              <p className="text-xs text-slate-500 mt-0.5">Send a 6-digit verification code to the master admin at every login. Requires a working Resend integration.</p>
            </div>
            <Switch
              checked={mfaEnabled}
              onCheckedChange={toggleMfa}
              disabled={savingMfa || (emailHealth && !emailHealth.healthy && !mfaEnabled)}
              data-testid="settings-mfa-toggle"
            />
          </div>

          {isMasterAdmin && emailHealth && (
            <div
              className={`mt-3 rounded-lg border p-3 text-xs ${
                emailHealth.healthy
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-red-300 bg-red-50 text-red-900"
              }`}
              data-testid="email-health-panel"
            >
              <div className="flex items-center gap-2 font-semibold">
                {emailHealth.healthy
                  ? <><CheckCircle size={16} weight="fill" /> Email delivery is configured</>
                  : <><Warning size={16} weight="fill" /> Email delivery is broken — MFA will not work</>}
              </div>
              <div className="mt-2 space-y-0.5 font-mono text-[11px]">
                <div>RESEND_API_KEY: <span className={emailHealth.resend_key_present ? "text-emerald-700" : "text-red-700 font-bold"}>{emailHealth.resend_key_present ? `set (${emailHealth.resend_key_length} chars)` : "EMPTY"}</span></div>
                <div>RESEND_FROM_EMAIL: <span className={emailHealth.is_sandbox_from ? "text-red-700 font-bold" : "text-emerald-700"}>{emailHealth.resend_from}</span></div>
              </div>
              {emailHealth.problems && emailHealth.problems.length > 0 && (
                <ul className="mt-2 list-disc list-inside space-y-1">
                  {emailHealth.problems.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              )}
              <div className="mt-2 flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={sendTestEmail}
                  disabled={testingEmail || !emailHealth.resend_key_present}
                  className="h-7 text-xs"
                  data-testid="email-test-send"
                >
                  {testingEmail ? "Sending…" : "Send test email"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={refreshEmailHealth}
                  className="h-7 text-xs"
                  data-testid="email-health-refresh"
                >
                  Re-check
                </Button>
              </div>
            </div>
          )}
        </Section>

        {/* Admin Users */}
        <Section title="Admin Accounts" Icon={IdentificationCard} testId="settings-section-admins">
          {!isMasterAdmin && (
            <p className="text-xs text-slate-500 mb-2">You're signed in as a regular admin — only the master admin can add or remove accounts.</p>
          )}
          {adminList.length > 0 && (
            <ul className="divide-y divide-slate-100 mb-3 rounded-md border border-slate-200" data-testid="admin-list">
              {adminList.map((a) => (
                <li key={a.email} className="px-3 py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-medium text-slate-900 truncate">{a.email}</span>
                      {a.is_master && (
                        <span className="text-[9px] uppercase tracking-wide bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full font-bold" data-testid="master-badge">Master</span>
                      )}
                      {a.email.toLowerCase() === (user?.email || "").toLowerCase() && (
                        <span className="text-[9px] uppercase tracking-wide bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full font-bold">You</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500">{a.role || "admin"}</div>
                  </div>
                  {isMasterAdmin && !a.is_master && (
                    <Button
                      onClick={() => requestDeleteAdmin(a.email)}
                      variant="outline"
                      size="sm"
                      disabled={adminBusy}
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      data-testid={`admin-delete-${a.email}`}
                    >
                      Remove
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {isMasterAdmin && !adminChallenge && (
            <Field label="Add new admin email">
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  placeholder="newadmin@example.com"
                  data-testid="admin-new-email"
                />
                <Button onClick={requestAddAdmin} disabled={adminBusy || !newAdminEmail} className="bg-emerald-800 hover:bg-emerald-900 text-white shrink-0" data-testid="admin-add-request">
                  Send code
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">New admin inherits your current password and master's MFA. They can change their own password later.</p>
            </Field>
          )}

          {adminChallenge && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 space-y-2" data-testid="admin-mfa-challenge">
              <div className="text-xs text-amber-900">
                Code sent to <span className="font-semibold">{adminChallenge.email_hint}</span> to {adminChallenge.mode === "add" ? "add" : "remove"} <span className="font-semibold">{adminChallenge.target_email}</span>
              </div>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={adminCode}
                onChange={(e) => setAdminCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="tracking-[6px] text-center font-mono"
                data-testid="admin-mfa-code"
              />
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => { setAdminChallenge(null); setAdminCode(""); }} variant="outline" data-testid="admin-mfa-cancel">Cancel</Button>
                <Button onClick={confirmAdminChange} disabled={adminBusy || adminCode.length < 6} className={adminChallenge.mode === "add" ? "bg-emerald-800 hover:bg-emerald-900 text-white" : "bg-red-700 hover:bg-red-800 text-white"} data-testid="admin-mfa-confirm">
                  Confirm {adminChallenge.mode}
                </Button>
              </div>
            </div>
          )}
        </Section>

        {/* Change Password */}
        <Section title="Change Password" Icon={Key} testId="settings-section-password">
          {isMasterAdmin && adminList.length > 1 && (
            <Field label="Change password for">
              <Select value={pw.target_email || user?.email || ""} onValueChange={(v) => setPw({ ...pw, target_email: v })}>
                <SelectTrigger data-testid="pw-target-email-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {adminList.map((a) => (
                    <SelectItem key={a.email} value={a.email} data-testid={`pw-target-${a.email}`}>
                      {a.email}{a.email.toLowerCase() === (user?.email || "").toLowerCase() ? " (you)" : ""}{a.is_master ? " · master" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Master admin can reset any admin's password. Authenticate with <span className="font-semibold">your own</span> current password below.</p>
            </Field>
          )}
          <Field label={isMasterAdmin && pw.target_email && pw.target_email !== user?.email ? "Your current password (to authorize)" : "Current Password"}>
            <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="settings-current-password" autoComplete="current-password" />
          </Field>
          <Field label="New Password (min 8 chars)">
            <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="settings-new-password" autoComplete="new-password" />
          </Field>
          <Field label="Confirm New Password">
            <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="settings-confirm-password" autoComplete="new-password" />
          </Field>
          <div className="flex justify-end">
            <Button onClick={changePassword} disabled={savingPw || !pw.current_password || !pw.new_password} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="settings-change-password-button">
              {savingPw ? "Updating…" : "Change Password"}
            </Button>
          </div>
        </Section>

        {/* Cloud Storage: Scaleway */}
        <Section title="Cloud Storage — Scaleway" Icon={CloudArrowUp} testId="settings-section-scaleway">
          <p className="text-xs text-slate-500 -mt-2">
            Object Storage (S3-compatible). Get credentials at
            <a href="https://console.scaleway.com/iam/api-keys" target="_blank" rel="noopener noreferrer" className="text-emerald-800 underline mx-1">Scaleway → IAM → API Keys</a>.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Access Key">
              <Input value={scaleway.access_key} onChange={(e) => setScaleway({ ...scaleway, access_key: e.target.value })} placeholder="SCWXXXXXXXXXXXXXXXXX" data-testid="settings-scaleway-access-key" />
            </Field>
            <Field label={`Secret Key ${scalewaySecretSet ? "(set ✓)" : ""}`}>
              <Input type="password" value={scaleway.secret_key} onChange={(e) => setScaleway({ ...scaleway, secret_key: e.target.value })} placeholder={scalewaySecretSet ? "•••• (leave blank to keep)" : "Secret key"} data-testid="settings-scaleway-secret-key" autoComplete="off" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Bucket Name">
              <Input value={scaleway.bucket} onChange={(e) => setScaleway({ ...scaleway, bucket: e.target.value })} placeholder="hydro-ledger-backups" data-testid="settings-scaleway-bucket" />
            </Field>
            <Field label="Region">
              <Input value={scaleway.region} onChange={(e) => setScaleway({ ...scaleway, region: e.target.value })} placeholder="fr-par / nl-ams / pl-waw" data-testid="settings-scaleway-region" />
            </Field>
          </div>
          <Field label="Endpoint URL (optional)">
            <Input value={scaleway.endpoint_url} onChange={(e) => setScaleway({ ...scaleway, endpoint_url: e.target.value })} placeholder="https://s3.fr-par.scw.cloud (auto)" data-testid="settings-scaleway-endpoint" />
          </Field>
          {scwStatus && (
            <div className={`text-sm rounded-md px-3 py-2 border flex items-center gap-2 ${scwStatus.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="scaleway-test-result">
              {scwStatus.ok ? <CheckCircle size={16} weight="duotone" /> : <XCircle size={16} weight="duotone" />}
              {scwStatus.msg}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button onClick={testScaleway} disabled={testingScw} variant="outline" data-testid="settings-scaleway-test">
              {testingScw ? "Testing…" : "Test Connection"}
            </Button>
            <Button onClick={saveScaleway} disabled={savingScw} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="settings-save-scaleway">
              {savingScw ? "Saving…" : "Save Scaleway"}
            </Button>
          </div>
        </Section>

        {/* Cloud Storage: NAS (S3-compatible) */}
        <Section title="Cloud Storage — Personal NAS (S3)" Icon={CloudArrowUp} testId="settings-section-nas">
          <p className="text-xs text-slate-500 -mt-2">
            Self-hosted S3-compatible storage (MinIO, TrueNAS, Synology C2, etc.). Provide your own endpoint,
            credentials, and bucket. Keeps your backups on-premises, GDPR-safe by default.
          </p>
          <Field label="Label (for your reference)">
            <Input value={nas.label} onChange={(e) => setNas({ ...nas, label: e.target.value })} placeholder="Personal NAS" data-testid="settings-nas-label" />
          </Field>
          <Field label="Endpoint URL">
            <Input value={nas.endpoint_url} onChange={(e) => setNas({ ...nas, endpoint_url: e.target.value })} placeholder="https://nas.mynetwork.local:9000" data-testid="settings-nas-endpoint" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Access Key">
              <Input value={nas.access_key} onChange={(e) => setNas({ ...nas, access_key: e.target.value })} placeholder="minioadmin" data-testid="settings-nas-access-key" />
            </Field>
            <Field label={`Secret Key ${nasSecretSet ? "(set ✓)" : ""}`}>
              <Input type="password" value={nas.secret_key} onChange={(e) => setNas({ ...nas, secret_key: e.target.value })} placeholder={nasSecretSet ? "•••• (leave blank to keep)" : "Secret key"} data-testid="settings-nas-secret-key" autoComplete="off" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Bucket Name">
              <Input value={nas.bucket} onChange={(e) => setNas({ ...nas, bucket: e.target.value })} placeholder="tnl-ledger-backups" data-testid="settings-nas-bucket" />
            </Field>
            <Field label="Region">
              <Input value={nas.region} onChange={(e) => setNas({ ...nas, region: e.target.value })} placeholder="us-east-1" data-testid="settings-nas-region" />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={nas.verify_tls !== false}
              onChange={(e) => setNas({ ...nas, verify_tls: e.target.checked })}
              className="rounded"
              data-testid="settings-nas-verify-tls"
            />
            Verify TLS certificate (uncheck for self-signed NAS certs)
          </label>
          {nasStatus && (
            <div className={`text-sm rounded-md px-3 py-2 border flex items-center gap-2 ${nasStatus.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="nas-test-result">
              {nasStatus.ok ? <CheckCircle size={16} weight="duotone" /> : <XCircle size={16} weight="duotone" />}
              {nasStatus.msg}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button onClick={testNas} disabled={testingNas} variant="outline" data-testid="settings-nas-test">
              {testingNas ? "Testing…" : "Test Connection"}
            </Button>
            <Button onClick={saveNas} disabled={savingNas} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="settings-save-nas">
              {savingNas ? "Saving…" : "Save NAS"}
            </Button>
          </div>
        </Section>


        {/* Logout */}
        <Section title="Account" Icon={SignOut} testId="settings-section-account">
          <Button onClick={doLogout} variant="outline" className="w-full border-red-200 text-red-700 hover:bg-red-50" data-testid="settings-logout-button">
            <SignOut size={18} weight="duotone" className="mr-2" /> Logout
          </Button>
        </Section>

        {/* Pre-Production Reset — wipes TEST sales cleanly, keeps expenses */}
        {isMasterAdmin && !productionStartedAt && (
          <Section title="Pre-Production Reset" Icon={Broom} testId="settings-section-preprod-reset">
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 space-y-3">
              <div>
                <div className="flex items-center gap-2">
                  <Warning size={18} weight="duotone" className="text-red-800" />
                  <div className="font-semibold text-red-900">Wipe ALL pre-production test data</div>
                </div>
                <p className="text-xs text-red-800 mt-1">
                  Deletes every sale, receipt, and expense entered before you click
                  <span className="font-semibold"> &ldquo;Mark today as the start of production&rdquo;</span>,
                  plus all their ledger vouchers. Also cleans the SIE-4 export. Business identity,
                  users, and settings are preserved. Only available before Production Start.
                </p>
              </div>

              {!resetExpanded ? (
                <Button
                  onClick={loadResetPreview}
                  variant="outline"
                  className="w-full border-red-300 text-red-800 hover:bg-red-100"
                  data-testid="preprod-reset-preview-button"
                >
                  <Broom size={16} weight="duotone" className="mr-2" />
                  Preview what will be wiped
                </Button>
              ) : (
                <>
                  {resetPreview && (
                    <div className="rounded-md bg-white border border-red-200 p-2.5 text-xs text-slate-800 space-y-1" data-testid="preprod-reset-preview">
                      <div>Test sales to delete: <span className="font-semibold text-red-800">{resetPreview.test_sales}</span></div>
                      <div>Test receipts to delete: <span className="font-semibold text-red-800">{resetPreview.test_receipts}</span></div>
                      <div>Test expenses to delete: <span className="font-semibold text-red-800">{resetPreview.test_expenses ?? 0}</span></div>
                      <div>Ledger vouchers to rebuild: <span className="font-semibold text-red-800">{resetPreview.test_vouchers}</span></div>
                      <div className="pt-1 border-t border-slate-100 text-emerald-800 space-y-0.5">
                        {"kept_sales" in resetPreview && <div>Non-test sales kept: <span className="font-semibold">{resetPreview.kept_sales}</span></div>}
                        {"kept_receipts" in resetPreview && <div>Non-test receipts kept: <span className="font-semibold">{resetPreview.kept_receipts}</span></div>}
                        <div>Non-test expenses kept: <span className="font-semibold">{resetPreview.kept_expenses ?? 0}</span></div>
                      </div>
                    </div>
                  )}
                  <Field label="Confirm password" Icon={Key}>
                    <Input
                      type="password"
                      value={resetPwd}
                      onChange={(e) => setResetPwd(e.target.value)}
                      placeholder="Master-admin password"
                      data-testid="preprod-reset-password"
                    />
                  </Field>
                  <Field label='Type "RESET TEST DATA" to confirm' Icon={ShieldCheck}>
                    <Input
                      value={resetPhrase}
                      onChange={(e) => setResetPhrase(e.target.value)}
                      placeholder="RESET TEST DATA"
                      className="font-mono"
                      data-testid="preprod-reset-phrase"
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => { setResetExpanded(false); setResetPreview(null); setResetPwd(""); setResetPhrase(""); }}
                      variant="outline"
                      data-testid="preprod-reset-cancel"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={doPreProductionReset}
                      disabled={resetBusy || resetPhrase !== "RESET TEST DATA" || !resetPwd}
                      className="bg-red-700 hover:bg-red-800 text-white"
                      data-testid="preprod-reset-confirm"
                    >
                      <Broom size={16} weight="duotone" className="mr-2" />
                      {resetBusy ? "Wiping…" : "Wipe test data"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </Section>
        )}

        {/* Production Start (Bokföringslagen 7-year retention) */}
        <Section title="Production Start (BFL 7-year retention)" Icon={Rocket} testId="settings-section-production-start">
          {productionStartedAt ? (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3" data-testid="production-already-started">
              <div className="flex items-center gap-2">
                <Lock size={20} weight="fill" className="text-emerald-800" />
                <div className="font-semibold text-emerald-900">Production started on {productionStartedAt}</div>
              </div>
              <p className="text-xs text-emerald-800 mt-2">
                All sales, expenses and receipts are now <span className="font-semibold">append-only</span> — no deletes, no purge.
                Records must be retained until <span className="font-semibold">{retentionUntil}</span> under Swedish accounting law
                (Bokföringslagen 1999:1078 ch. 7 §2). Excel exports include a tamper-evident SHA-256 fingerprint footer.
              </p>
              {settings?.production_started_by && (
                <p className="text-[11px] text-emerald-700 mt-1">Started by {settings.production_started_by}</p>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-3">
              <div>
                <div className="flex items-center gap-2">
                  <Rocket size={18} weight="duotone" className="text-amber-800" />
                  <div className="font-semibold text-amber-900">Mark today as the start of production</div>
                </div>
                <p className="text-xs text-amber-800 mt-1">
                  From the day you confirm, all transactional data becomes immutable — no edits, no purges, no accidental deletes — for 7 years.
                  This satisfies <span className="font-semibold">Bokföringslagen</span> retention requirements for audited bookkeeping.
                </p>
              </div>
              {!prodChallenge ? (
                <>
                  <Field label="Effective production start date" Icon={Rocket}>
                    <Input type="date" value={prodStartDate} onChange={(e) => setProdStartDate(e.target.value)} max={new Date().toISOString().slice(0, 10)} data-testid="prod-start-date" />
                  </Field>
                  <Field label="Confirm your password" Icon={Key}>
                    <Input type="password" value={prodPwd} onChange={(e) => setProdPwd(e.target.value)} placeholder="Admin password" data-testid="prod-start-password" />
                  </Field>
                  <Button onClick={requestProductionStart} disabled={prodBusy} className="w-full bg-amber-700 hover:bg-amber-800 text-white" data-testid="prod-start-request">
                    <Rocket size={18} weight="duotone" className="mr-2" />
                    {prodBusy ? "Sending code…" : "Send verification code"}
                  </Button>
                </>
              ) : (
                <>
                  <div className="rounded-md bg-white border border-emerald-200 p-2.5 text-xs text-emerald-900">
                    6-digit code sent to <span className="font-semibold">{prodChallenge.email_hint}</span> (valid 10 min)
                  </div>
                  <Field label="Enter verification code" Icon={ShieldCheck}>
                    <Input
                      type="text" inputMode="numeric" maxLength={6} value={prodCode}
                      onChange={(e) => setProdCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456" className="tracking-[6px] text-center font-mono text-lg"
                      data-testid="prod-start-code"
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-2">
                    <Button onClick={() => { setProdChallenge(null); setProdCode(""); }} variant="outline" data-testid="prod-start-cancel">
                      Cancel
                    </Button>
                    <Button onClick={confirmProductionStart} disabled={prodBusy || prodCode.length < 6} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="prod-start-confirm">
                      <Lock size={18} weight="duotone" className="mr-2" />
                      {prodBusy ? "Locking…" : "Lock & start"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </Section>
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ title, Icon, children, testId }) => (
  <section className="space-y-3 pt-4 mt-2 border-t border-slate-200 first-of-type:border-t-0 first-of-type:pt-0 first-of-type:mt-0" data-testid={testId}>
    <div className="flex items-center gap-2">
      <Icon size={18} weight="duotone" className="text-emerald-800" />
      <h3 className="font-heading text-sm font-bold uppercase tracking-[0.18em] text-slate-700">{title}</h3>
    </div>
    {children}
  </section>
);

const Field = ({ label, Icon, children }) => (
  <div>
    <Label className="text-slate-700 flex items-center gap-1.5">
      {Icon && <Icon size={14} weight="duotone" className="text-slate-500" />}
      {label}
    </Label>
    <div className="mt-1">{children}</div>
  </div>
);
